import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getReportStatus } from "shared/api/function_data";
import { queryKeys } from "shared/lib/queryClient";

function getAllSidebarKeys(sidebarOptions) {
  if (!sidebarOptions) return [];
  const keys = [];
  for (const section of Object.values(sidebarOptions)) {
    for (const item of section.items || []) {
      keys.push(item.key);
    }
  }
  return keys;
}

/**
 * Fetches report upload status for all sidebar keys.
 * Backed by React Query and keyed by function/year/week (NOT by the key list),
 * so flipping between weeks reuses cache. A successful upload invalidates this
 * query (see invalidateAfterUpload) so the sidebar check/x icons refresh.
 *
 * Return shape is unchanged: { reportStatus, reportStatusLoading, refreshReportStatus }
 */
export function useReportStatus(functionId, year, week, functionSidebar) {
  const sidebarKeys = useMemo(
    () => getAllSidebarKeys(functionSidebar),
    [functionSidebar]
  );

  const enabled = !!functionId && sidebarKeys.length > 0;

  const { data, isFetching, refetch } = useQuery({
    queryKey: queryKeys.reportStatus(functionId, year, week),
    queryFn: () => getReportStatus(functionId, year, week, sidebarKeys),
    enabled,
  });

  const refreshReportStatus = useCallback(async () => {
    if (!enabled) return;
    await refetch();
  }, [enabled, refetch]);

  return {
    reportStatus: data ?? {},
    reportStatusLoading: enabled ? isFetching : false,
    refreshReportStatus,
  };
}
